import { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { Tooltip } from "primereact/tooltip";
import localeText from "../../locale";
import ConfirmDocument from "../../components/ConfirmDocument/ConfirmDocument";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import useGetUserInfo from "../../hooks/useGetUserInfo";
import AdminSidebar from "./components/AdminSidebar/AdminSidebar";
import { ADMIN_ROLES, flattenMenuLinks, getAdminPanelMenu, isPathActive } from "./config/adminPanelMenu";
import { getAdminPanelFallbackPath, getAdminPanelRoutes } from "./config/adminPanelRoutes";
import "../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import "./AdminPanel.css";
import "./styles/adminList.css";
import "./styles/adminForm.css";

const AdminPanel = ({ userType = ADMIN_ROLES.ADMIN, onSwitchVersion }) => {
  const { showErrorToast } = useToastContext();
  const { userInfo } = useGetUserInfo({
    onUserInfoError: (messageCode) => showErrorToast({ code: messageCode }),
  });
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const copyTooltipRef = useRef();

  const menu = useMemo(() => getAdminPanelMenu(userType), [userType]);
  const routes = useMemo(() => getAdminPanelRoutes(userType), [userType]);
  const fallbackPath = getAdminPanelFallbackPath(userType);

  const currentLink = useMemo(
    () => flattenMenuLinks(menu).find((link) => isPathActive(link, pathname)),
    [menu, pathname],
  );

  useEffect(() => {
    setMobileMenuOpen(false);
    copyTooltipRef.current?.updateTargetEvents();
  }, [pathname]);

  return (
    <div className="admin-panel">
      <ConfirmDocument />

      <Tooltip
        ref={copyTooltipRef}
        target=".block-action-copy"
        position="bottom"
        content="Copied to clipboard"
        event="focus"
      />

      <AdminSidebar
        menu={menu}
        userInfo={userInfo}
        userType={userType}
        isOpen={mobileMenuOpen}
        onNavigate={() => setMobileMenuOpen(false)}
        onSwitchVersion={onSwitchVersion}
      />

      {mobileMenuOpen && (
        <div className="admin-panel__mask" onClick={() => setMobileMenuOpen(false)} />
      )}

      <div className="admin-panel__main">
        <header className="admin-panel__header">
          <button
            type="button"
            className="admin-panel__menu-btn"
            onClick={() => setMobileMenuOpen(true)}
            aria-label={localeText("adminPanelOpenMenu")}
          >
            <i className="pi pi-bars" aria-hidden="true" />
          </button>
          <div className="admin-panel__title-group">
            {currentLink?.parentLabel && (
              <p className="admin-panel__breadcrumb">{currentLink.parentLabel}</p>
            )}
            <h1 className="admin-panel__title">
              {currentLink?.label || localeText("adminDashboard")}
            </h1>
          </div>
          <span className="admin-panel__beta">{localeText("adminPanelBeta")}</span>
          <button
            type="button"
            className="admin-panel__switch"
            onClick={onSwitchVersion}
            aria-label={localeText("adminPanelUseClassic")}
            title={localeText("adminPanelUseClassic")}
          >
            <i className="pi pi-history" aria-hidden="true" />
            <span>{localeText("adminPanelClassicVersion")}</span>
          </button>
        </header>

        <main className="admin-panel__content">
          <Switch>
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                exact={!!route.exact}
                render={() => route.render({ userInfo })}
              />
            ))}
            <Redirect to={fallbackPath} />
          </Switch>
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
